import React, { ComponentProps } from 'react';
import { asFetchableView, useViewContext } from '../../../_lib/ViewFetcher';
import {
  CheckBoxParam,
  Checkbox,
  Column,
  Fieldset,
  Form,
  Input,
  Row,
  SonForm,
  SubmitButton,
  TextParam,
  useForm,
  NumberParam,
} from '../../../_lib/Form2';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { Hint } from '../../../_lib/components/Hint';
import { RowModifier } from '../../../_lib/Form2/grid/Row/Row';
import { ColumnModifier } from '../../../_lib/Form2/grid/Column/Column';

type FormApplicationSettingsEditProps = {
  form: SonForm<
    | TextParam<'emailAddress1'>
    | TextParam<'emailAddress2'>
    | TextParam<'emailAddress3'>
    | TextParam<'replyToAddress'>
    | CheckBoxParam<'notifyCreatorPortal'>
    | CheckBoxParam<'notifyCreatorEmail'>
    | CheckBoxParam<'notifyManagersPortal'>
    | CheckBoxParam<'notifyManagersEmail'>
    | NumberParam<'resultExpirationPeriodDays'>
  >;
  refresh: () => void;
};

function FormApplicationSettingsEdit({ form }: FormApplicationSettingsEditProps) {
  const { t } = useSonTranslation('FormApplicationSettings');
  const { refresh } = useViewContext();

  const { formProps, params, fetchState } = useForm({ form, onSuccess: refresh });

  const notifyCreatorPortal = params.getByName('notifyCreatorPortal');
  const notifyCreatorEmail = params.getByName('notifyCreatorEmail');
  const notifyManagersPortal = params.getByName('notifyManagersPortal');
  const notifyManagersEmail = params.getByName('notifyManagersEmail');
  const emailAddress1 = params.getByName('emailAddress1');
  const emailAddress2 = params.getByName('emailAddress2');
  const emailAddress3 = params.getByName('emailAddress3');
  const replyToAddress = params.getByName('replyToAddress');
  const resultExpirationPeriodDays = params.getByName('resultExpirationPeriodDays');

  return (
    <div className="form-application-settings__edit-container">
      <Form {...formProps}>
        <Fieldset legend={t('form.notificationsFieldsetLegend')}>
          <Row>
            <Hint message={t('form.emailHint')} />
          </Row>
          <Row modifier={RowModifier.GRID_2_COLUMNS}>
            <Column modifier={ColumnModifier.NO_MARGIN}>
              <Checkbox
                label={
                  <>
                    {t('form.notifyCreatorPortalLabel')}{' '}
                    <span className="font-regular-15-19">{t('form.notifyCreatorPortalHint')}</span>
                  </>
                }
                param={notifyCreatorPortal}
              />
            </Column>
            <Column modifier={ColumnModifier.NO_MARGIN}>
              <Checkbox
                label={
                  <>
                    {t('form.notifyCreatorEmailLabel')}{' '}
                    <span className="font-regular-15-19">{t('form.notifyCreatorEmailHint')}</span>
                  </>
                }
                param={notifyCreatorEmail}
              />
            </Column>
          </Row>
          <Row modifier={[RowModifier.GRID_2_COLUMNS]}>
            <Column modifier={ColumnModifier.NO_MARGIN}>
              <Checkbox
                label={
                  <>
                    {t('form.notifyManagersPortalLabel')}{' '}
                    <span className="font-regular-15-19">{t('form.notifyManagersPortalHint')}</span>
                  </>
                }
                param={notifyManagersPortal}
              />
            </Column>
            <Column modifier={ColumnModifier.NO_MARGIN}>
              <Checkbox
                label={
                  <>
                    {t('form.notifyManagersEmailLabel')}{' '}
                    <span className="font-regular-15-19">{t('form.notifyManagersEmailHint')}</span>
                  </>
                }
                param={notifyManagersEmail}
              />
            </Column>
          </Row>
        </Fieldset>
        <Fieldset legend={t('form.fieldsetLegend')}>
          <Row>
            <Input label={t('form.emailAddress1Label')} param={emailAddress1} />
          </Row>
          <Row>
            <Input label={t('form.emailAddress2Label')} param={emailAddress2} />
          </Row>
          <Row>
            <Input label={t('form.emailAddress3Label')} param={emailAddress3} />
          </Row>
          <Row>
            <Input label={t('form.replyToAddressLabel')} param={replyToAddress} />
          </Row>
        </Fieldset>
        <Fieldset legend={t('form.fieldsetExpirationLegend')}>
          <Row>
            <Input label={t('form.resultExpirationPeriodDaysLabel')} param={resultExpirationPeriodDays} />
          </Row>
        </Fieldset>
        <SubmitButton
          buttonLabel={t('form.submitButtonLabel')}
          messageSuccess={t('phrases:changeSuccesfull')}
          messageSending={t('phrases:processIsCurrentlyRunningWithDots')}
          messageFail={t('phrases:changesCouldNotBeSaved')}
          fetchState={fetchState}
        />
      </Form>
    </div>
  );
}

export const FormApplicationSettingsEditView = asFetchableView(FormApplicationSettingsEdit);
export type FormApplicationSettingsEditView = ComponentProps<typeof FormApplicationSettingsEditView>;
